import { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { API_BASE } from '@/utils/api';

const ThemeContext = createContext(null);

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

const themes = {
  light: { name: 'Claro', value: 'light', icon: '☀️', description: 'Limpio y profesional' },
  dark: { name: 'Oscuro', value: 'dark', icon: '🌙', description: 'Moderno y elegante' },
  ocean: { name: 'Océano', value: 'ocean', icon: '🌊', description: 'Azul profundo profesional' },
  sunset: { name: 'Atardecer', value: 'sunset', icon: '🌅', description: 'Cálido y acogedor' },
  emerald: { name: 'Esmeralda', value: 'emerald', icon: '💎', description: 'Fresco y vibrante' },
  rose: { name: 'Rosa', value: 'rose', icon: '🌸', description: 'Elegante y sofisticado' },
};

export const designs = {
  classic: { name: 'Clásico', value: 'classic', description: 'El diseño de siempre' },
  modern: { name: 'Moderno', value: 'modern', description: 'Menú lateral oscuro, tarjetas limpias' },
};

const COLOR_CLASSES = ['dark', 'ocean', 'sunset', 'emerald', 'rose'];
const stored = (k, d) => localStorage.getItem(k) ?? d;

export const ThemeProvider = ({ children }) => {
  const [theme, setThemeState] = useState(() => stored('qrmesa-theme', 'light'));
  const [design, setDesignState] = useState(() => stored('qrmesa-design', 'classic'));
  const [modernDark, setModernDarkState] = useState(() => stored('qrmesa-modern-dark', '0') === '1');
  const [highContrast, setHighContrastState] = useState(() => stored('qrmesa-hc', '0') === '1');
  const [locked, setLocked] = useState({ theme: false, high_contrast: false });
  const [businessDesigns, setBusinessDesigns] = useState(null);
  const [designOverride, setDesignOverride] = useState(null);
  const synced = useRef(false);

  const activeDesign = designOverride || design;

  useEffect(() => {
    const root = document.documentElement;
    root.classList.remove(...COLOR_CLASSES, 'theme-modern', 'hc');
    root.removeAttribute('data-theme');
    root.removeAttribute('data-accent');
    if (activeDesign === 'modern') {
      root.classList.add('theme-modern');
      root.setAttribute('data-accent', theme);
      if (modernDark) root.classList.add('dark');
    } else if (theme !== 'light') {
      root.classList.add(theme);
      root.setAttribute('data-theme', theme);
    }
    if (highContrast) root.classList.add('hc');
    localStorage.setItem('qrmesa-theme', theme);
    localStorage.setItem('qrmesa-design', design);
    localStorage.setItem('qrmesa-modern-dark', modernDark ? '1' : '0');
    localStorage.setItem('qrmesa-hc', highContrast ? '1' : '0');
  }, [theme, design, activeDesign, modernDark, highContrast]);

  const hydrate = useCallback((data) => {
    synced.current = !!data;
    if (!data) {
      setLocked({ theme: false, high_contrast: false });
      setBusinessDesigns(null);
      return;
    }
    setDesignState(data.theme);
    setThemeState(data.accent);
    setModernDarkState(data.dark);
    setHighContrastState(data.high_contrast);
    setLocked(data.locked);
    setBusinessDesigns(data.business);
  }, []);

  const persist = useCallback(async (change) => {
    if (!synced.current) return;
    try {
      const { data } = await axios.put(`${API_BASE}/appearance/me`, change);
      hydrate(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'No se pudo guardar tu apariencia');
      axios.get(`${API_BASE}/appearance/me`).then(r => hydrate(r.data)).catch(() => {});
    }
  }, [hydrate]);

  const setTheme = (v) => { setThemeState(v); persist({ accent: v }); };
  const setDesign = (v) => { setDesignState(v); persist({ theme: v }); };
  const setModernDark = (v) => { setModernDarkState(v); persist({ dark: v }); };
  const setHighContrast = (v) => { setHighContrastState(v); persist({ high_contrast: v }); };

  const toggleTheme = () => {
    if (activeDesign === 'modern') {
      setModernDark(!modernDark);
      return;
    }
    const keys = Object.keys(themes);
    setTheme(keys[(keys.indexOf(theme) + 1) % keys.length]);
  };

  const value = {
    theme, setTheme, toggleTheme, themes, currentTheme: themes[theme],
    design: activeDesign, userDesign: design, setDesign, designs,
    modernDark, setModernDark, highContrast, setHighContrast,
    isDark: activeDesign === 'modern' ? modernDark : theme !== 'light',
    locked, businessDesigns, hydrate, setDesignOverride,
  };

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
};

// Las páginas del cliente (QR) usan el tema que eligió el negocio para el menú
export const useMenuDesign = (menuTheme) => {
  const { setDesignOverride } = useTheme();
  useEffect(() => {
    if (!menuTheme) return undefined;
    setDesignOverride(menuTheme);
    return () => setDesignOverride(null);
  }, [menuTheme, setDesignOverride]);
};

export default ThemeContext;
